import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Utensils, Scale, ClipboardCheck, MessageSquare, ArrowRight } from 'lucide-react';

const steps = [
  {
    id: 'meal_plan',
    title: 'Your Meal Plan',
    description: 'View your personalized meals here. Use the swap button inside any recipe to find alternatives.',
    icon: Utensils,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    visual: {
      type: 'mockup',
      label: 'Meal Plan',
      sublabel: 'Tap to view & swap recipes',
      feature: 'Swap Recipe',
      featureIcon: '🔄'
    }
  },
  {
    id: 'weight_log',
    title: 'Log Weight Daily',
    description: 'Track your progress by logging weight every morning. Takes 30 seconds and helps your coach adjust your plan.',
    icon: Scale,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    visual: {
      type: 'mockup',
      label: 'Weight Log',
      sublabel: 'Quick daily tracking',
      feature: 'Log daily',
      featureIcon: '📊'
    }
  },
  {
    id: 'check_in',
    title: 'Weekly Check-Ins',
    description: 'Complete your check-in between Wednesday-Thursday each week. Your coach reviews it and adjusts your plan if needed.',
    icon: ClipboardCheck,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    visual: {
      type: 'mockup',
      label: 'Weekly Check-in',
      sublabel: 'Opens Wed-Thu',
      feature: 'Coach reviews',
      featureIcon: '✓'
    }
  },
  {
    id: 'messages',
    title: 'Message Anytime',
    description: 'Have questions? Need support? Message your coach anytime. Response within 24 hours.',
    icon: MessageSquare,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    visual: {
      type: 'mockup',
      label: 'Messages',
      sublabel: 'Direct coach support',
      feature: '24hr response',
      featureIcon: '💬'
    }
  }
];

// Mockup component that shows a visual representation
function FeatureMockup({ icon: Icon, iconBg, iconColor, visual }) {
  return (
    <div className="flex flex-col items-center gap-4 my-8">
      {/* Main feature card mockup */}
      <div className="relative">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-64 border-2 border-gray-100 transform hover:scale-105 transition-transform">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Icon className={`h-7 w-7 ${iconColor}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 text-base">{visual.label}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{visual.sublabel}</p>
            </div>
          </div>
          
          {/* Feature indicator */}
          {visual.feature && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-lg">{visual.featureIcon}</span>
                <span className="font-medium">{visual.feature}</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Pointer arrow */}
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
          <ArrowRight className="h-6 w-6 text-blue-500 rotate-90 animate-bounce" />
        </div>
      </div>
      
      {/* Location hint */}
      <div className="text-sm text-gray-500 mt-4 text-center">
        Find this on your dashboard
      </div>
    </div>
  );
}

export default function QuickStartGuide({ isOpen, onClose, autoShow = false, userEmail = null }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  // Mark as shown in localStorage (per-user)
  useEffect(() => {
    console.log('[QuickStartGuide] isOpen:', isOpen, 'autoShow:', autoShow, 'userEmail:', userEmail);
    if (isOpen && autoShow && userEmail) {
      const flagKey = `quick_start_shown_${userEmail}`;
      console.log('[QuickStartGuide] 🚨 SETTING flag:', flagKey);
      localStorage.setItem(flagKey, 'true');
    }
  }, [isOpen, autoShow, userEmail]);

  const handleNext = () => {
    if (isAnimating) return;
    if (isLastStep) {
      onClose();
      return;
    }
    setDirection(1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setIsAnimating(false);
    }, 150);
  };

  const handlePrev = () => {
    if (isAnimating || isFirstStep) return;
    setDirection(-1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setIsAnimating(false);
    }, 150);
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 border-2">
        {/* Content */}
        <div className="p-6">
          {/* Main Title */}
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              What should I do now?
            </h1>
            <p className="text-xs text-gray-500">Quick guide to get you started</p>
          </div>

          {/* Header */}
          <div className="text-center mb-4 pt-3 border-t border-gray-100">
            <div className={`w-14 h-14 ${step.iconBg} rounded-xl flex items-center justify-center mx-auto mb-3`}>
              <StepIcon className={`h-7 w-7 ${step.iconColor}`} />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {step.title}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto">
              {step.description}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-6 mb-4">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isAnimating && index !== currentStep) {
                    setDirection(index > currentStep ? 1 : -1);
                    setIsAnimating(true);
                    setTimeout(() => {
                      setCurrentStep(index);
                      setIsAnimating(false);
                    }, 150);
                  }
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'bg-gray-800 w-8'
                    : 'bg-gray-300 w-2 hover:bg-gray-400'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`flex-1 h-10 ${
                isFirstStep ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              onClick={handleNext}
              className="flex-1 h-10 bg-gray-900 hover:bg-gray-800 text-white"
            >
              {isLastStep ? (
                "Got it!"
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Step counter */}
          <p className="text-center text-xs text-gray-400 mt-3">
            {currentStep + 1} of {steps.length}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
